/**
 * Calculator Error Card
 *
 * Displays error states with retry button.
 */

import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CalculatorError } from '../lib/types';
import { cn } from '@/lib/utils';

interface CalculatorErrorCardProps {
  error: CalculatorError;
  errorMessage: string | null;
  onRetry: () => void;
  isLoading: boolean;
  className?: string;
}

export function CalculatorErrorCard({
  error,
  errorMessage,
  onRetry,
  isLoading,
  className,
}: CalculatorErrorCardProps) {
  const { t } = useTranslation('common');

  // Get error title based on error type
  const getErrorTitle = (): string => {
    switch (error) {
      case 'INVALID_PLATE':
        return t('calculator.errorInvalidPlate', 'Ongeldig kenteken');
      case 'NOT_FOUND':
        return t('calculator.errorNotFound', 'Kenteken niet gevonden');
      case 'RATE_LIMITED':
        return t('calculator.errorRateLimited', 'Te veel verzoeken');
      case 'RDW_UNAVAILABLE':
        return t('calculator.errorRdwUnavailable', 'RDW onbereikbaar');
      case 'MISSING_DATA':
        return t('calculator.errorMissingData', 'Onvoldoende gegevens');
      default:
        return t('calculator.errorGeneric', 'Er ging iets mis');
    }
  };

  return (
    <div className={cn('rounded-lg border border-destructive/50 bg-destructive/10 p-4', className)}>
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-destructive/20">
          <AlertCircle className="h-4 w-4 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-destructive">{getErrorTitle()}</h3>
          {errorMessage && <p className="text-sm text-destructive/80 mt-1">{errorMessage}</p>}
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={isLoading}
            className="mt-3"
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
            {t('calculator.retry', 'Opnieuw proberen')}
          </Button>
        </div>
      </div>
    </div>
  );
}
